import React from "react";
import { KeyboardArrowRight } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Typography,
} from "@mui/material";
import {
  WorkflowCanvas,
  WorkflowTask,
  describeWorkflowPerson,
} from "./workflowBuilderData";

interface WorkflowTaskStageProps {
  canvas: WorkflowCanvas;
  completed: Record<string, boolean>;
  onToggle: (taskId: string, done: boolean) => void;
  onContinue: () => void;
  /** Tasks that can't be toggled — on a real case a completed task is final (there is no
   *  un-complete), and a task the case hasn't materialized yet can't be completed. */
  lockedTaskIds?: string[];
  /** An action is in flight: every checkbox and Continue are disabled. */
  busy?: boolean;
  banner?: React.ReactNode;
}

const assigneeOf = (task: WorkflowTask): string =>
  task.assigneeSource === "specific-user" && !task.assignee
    ? "Unassigned"
    : describeWorkflowPerson(task.assigneeSource, task.assignee);

const WorkflowTaskStage: React.FC<WorkflowTaskStageProps> = ({
  canvas,
  completed,
  onToggle,
  onContinue,
  lockedTaskIds = [],
  busy = false,
  banner,
}) => {
  const allDone = canvas.tasks.every((task) => completed[task.id]);

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    onToggle(event.target.name, event.target.checked);
  };

  return (
    <>
      <Typography component="h3" className="workflow-run__section-title">
        {canvas.name}
      </Typography>
      <Typography className="workflow-run__hint workflow-run__hint--flush">
        Complete every task below to continue.
      </Typography>

      {canvas.tasks.length === 0 ? (
        <Typography className="workflow-run__empty-stage">
          This canvas has no tasks yet. Add them in the Builder to show a checklist here.
        </Typography>
      ) : (
        <Box className="workflow-run__checklist">
          {canvas.tasks.map((task) => {
            const isUrl = /^https?:\/\//i.test(task.description);

            return (
              <Box key={task.id} className="workflow-run__check-item">
                <FormControlLabel
                  control={
                    <Checkbox
                      name={task.id}
                      checked={Boolean(completed[task.id])}
                      onChange={handleToggle}
                      disabled={busy || lockedTaskIds.includes(task.id)}
                    />
                  }
                  label={
                    <span className="workflow-run__check-copy">
                      <strong>{task.title || "Untitled task"}</strong>
                      <span>
                        {isUrl ? (
                          <Link href={task.description} target="_blank" rel="noreferrer">
                            {task.description}
                          </Link>
                        ) : (
                          task.description
                        )}
                        {task.description ? " · " : ""}
                        Assigned to {assigneeOf(task)}
                      </span>
                    </span>
                  }
                />
              </Box>
            );
          })}
        </Box>
      )}

      {banner}

      <Box className="workflow-run__actions workflow-run__actions--end">
        <Button
          variant="contained"
          endIcon={<KeyboardArrowRight />}
          disabled={!allDone || busy}
          onClick={onContinue}
        >
          Continue
        </Button>
      </Box>
    </>
  );
};

export default WorkflowTaskStage;
